// @flow
import React from 'react'
import I18n from 'i18n-react'

// components
import { SupplementaryInsuranceItem } from '../../Components'
import type { ProductContent } from '../Types/ProductContent'
import type { ExpandedSpecificationDTO } from '../Types/Field'
import type { InputHelpsType } from '../Types/InputHelpType'
import type { ValueObject } from '../Types/Values'
import type { ValidationErrorType } from '../Types/ValidationError'
import type { OneQuoteDocuments } from '../Types/DocumentTypes'
import type { TariffContentType } from '../Types/TariffsContentType'
import type { ActiveSettingsType, IdentifyObject } from '../Types/SettingsTypes'
import type { AsMutable } from '../Types/ImmutableTypes'

// types
type SupplementaryInsurancesProps = {
  activeSettings: ActiveSettingsType & AsMutable<ActiveSettingsType>,
  allowedTags?: Array<string>,
  calculatorName?: string,
  content: ProductContent,
  debug?: boolean,
  documents: OneQuoteDocuments,
  hideDisabled?: boolean,
  identifyObject?: IdentifyObject,
  inputHelps?: InputHelpsType,
  isBackOffice?: boolean,
  narrow?: boolean,
  onChangeSupplementaryInsurances: (data: Object, paths: Array<string>) => void,
  onChangeUserFormData: (data: Object, paths: Array<string>) => void,
  onChangeValue: (key: string, value: string | Object, pathArray: Array<string>) => void,
  onRefreshCoveragePrices: (data: any) => void,
  onRemoveSupplementaryInsurance: (string) => void,
  pushIsPristine: boolean,
  quoteId: string,
  specification?: ExpandedSpecificationDTO,
  supplementaryInsurances: { [key: string]: any },
  tariffContent: TariffContentType,
  templateVariables?: ValueObject,
  token?: string,
  validationErrors: ValidationErrorType,
  valueNames: { [key: string]: { [key: string]: string } },
}

type SupplementaryInsurancesState = {
  prevContent: ProductContent | {},
  prevSpecification: ExpandedSpecificationDTO | {},
  contentParsed: any,
  specificationParsed: any,
}

export default class SupplementaryInsurances extends React.Component<
  SupplementaryInsurancesProps,
  SupplementaryInsurancesState,
> {
  static getDerivedStateFromProps = (
    nextProps: SupplementaryInsurancesProps,
    prevState: SupplementaryInsurancesState,
  ) => {
    const { content } = nextProps
    if (
      !!nextProps.specification &&
      !!content &&
      (JSON.stringify(content) !== JSON.stringify(prevState.prevContent) ||
        JSON.stringify(nextProps.specification) !== JSON.stringify(prevState.prevSpecification))
    ) {
      const newState = {
        prevSpecification: nextProps.specification,
      }
      newState.allDisabled = nextProps.specification.fields.every((supplementary) => !supplementary.enabled)
      newState.contentParsed = content.fieldOrder.reduce((acc, insuranceKey) => {
        let componentOverwriteKey = insuranceKey
        if (content.components[`${insuranceKey}${nextProps.calculatorName}`]) {
          componentOverwriteKey = `${insuranceKey}${nextProps.calculatorName}`
        }
        let fieldNamesOverwriteKey = insuranceKey
        if (content.fieldNames[`${insuranceKey}${nextProps.calculatorName}`]) {
          fieldNamesOverwriteKey = `${insuranceKey}${nextProps.calculatorName}`
        }
        if (!content.components[componentOverwriteKey].children) {
          acc[insuranceKey] = {
            fieldNames: { [insuranceKey]: content.fieldNames[fieldNamesOverwriteKey] },
            fieldOrder: [insuranceKey],
            types: { ...content.types },
            valueNames: {},
            components: {
              ...content.components,
              [insuranceKey]: content.components[componentOverwriteKey],
            },
          }
        } else {
          const kids: Array<string> = content.components[componentOverwriteKey].children
          const parentType = {
            [insuranceKey]: {
              fieldNames: {
                [insuranceKey]: content.fieldNames[fieldNamesOverwriteKey],
              },
              fieldOrder: [insuranceKey, ...kids],
              components: {
                ...content.components,
                [insuranceKey]: content.components[componentOverwriteKey],
              },
              types: { ...content.types },
            },
          }
          kids.map((childrenKey) => {
            let childrenComponentOverwriteKey = childrenKey
            if (content.components[`${childrenKey}${nextProps.calculatorName}`]) {
              childrenComponentOverwriteKey = `${childrenKey}${nextProps.calculatorName}`
            }
            parentType[insuranceKey].fieldNames[childrenKey] = content.fieldNames[childrenKey]
            parentType[insuranceKey].components[childrenKey] = content.components[childrenComponentOverwriteKey]
            parentType[insuranceKey].types[childrenKey] = content.types[childrenKey]
          })
          acc[insuranceKey] = {
            types: parentType,
            fieldNames: { [insuranceKey]: content.fieldNames[fieldNamesOverwriteKey] },
            fieldOrder: [insuranceKey],
            components: {
              [insuranceKey]: {
                ...content.components[componentOverwriteKey],
                name: '',
              },
            },
          }
        }
        return acc
      }, {})
      newState.specificationParsed = content.fieldOrder.reduce((acc, insuranceKey) => {
        let componentOverwriteKey = insuranceKey
        if (content.components[`${insuranceKey}${nextProps.calculatorName}`]) {
          componentOverwriteKey = `${insuranceKey}${nextProps.calculatorName}`
        }
        if (!content.components[componentOverwriteKey].children) {
          acc[insuranceKey] = {
            fields: nextProps.specification.fields.filter((field) => field.name === insuranceKey),
            name: '<root>',
            types: nextProps.specification.types,
          }
        } else {
          const kids: Array<string> = content.components[insuranceKey].children
          const kidsWithParent = [...kids, insuranceKey]
          let parentField = nextProps.specification.fields.filter((field) => field.name === insuranceKey)[0]
          let anyEnabled = nextProps.specification.fields.reduce((accu, field) => {
            if (kidsWithParent.includes(field.name)) {
              return accu || field.enabled
            }
            return accu
          }, false)
          const parentSpec = {
            name: insuranceKey,
            types: nextProps.specification.types,
            fields: nextProps.specification.fields.filter((field) => kidsWithParent.includes(field.name)),
          }
          parentField = {
            ...parentField,
            enabled: anyEnabled,
            domain: {
              ...parentField.domain,
              specification: parentSpec,
            },
          }
          acc[insuranceKey] = {
            types: [parentSpec],
            fields: [parentField],
            name: '<root>',
          }
        }
        return acc
      }, {})
      return newState
    }
    return null
  }

  state = {
    prevContent: {},
    prevSpecification: {},
    contentParsed: {},
    specificationParsed: {},
    allDisabled: false,
  }

  handleRemoveInsurance = (id: string) => {
    this.props.onRemoveSupplementaryInsurance(id)
  }

  handleOnChange = (key: string, value: string | Object, pathArray: Array<string>) => {
    this.props.onChangeValue(key, value, pathArray)
  }

  handleChangeSupplementaryInsurances = (data: Object, paths: Array<string>) => {
    this.props.onChangeSupplementaryInsurances(data, paths)
  }

  handleRefreshCoveragePrices = (data: any) => {
    this.props.onRefreshCoveragePrices(data)
  }

  handleChangeUserFormData = (data: Object, paths: Array<string>) => {
    this.props.onChangeUserFormData(data, paths)
  }

  render() {
    const {
      activeSettings,
      allowedTags,
      content,
      debug,
      documents,
      hideDisabled,
      identifyObject,
      inputHelps,
      isBackOffice,
      narrow,
      onQuoteValidate,
      pushIsPristine,
      resources,
      supplementaryInsurances,
      tariffContent,
      templateVariables,
      token,
      validationErrors,
      valueNames,
    } = this.props
    const { specificationParsed, contentParsed, allDisabled } = this.state
    if (!content || !content.fieldOrder) {
      return null
    }

    if (allDisabled) {
      return <div>{I18n.translate('general.noInsurancesAvailable')}</div>
    }

    return content.fieldOrder.map((insuranceKey) => {
      const components = contentParsed[insuranceKey].components[insuranceKey]
      return (
        <SupplementaryInsuranceItem
          activeSettings={activeSettings}
          allowedTags={allowedTags}
          content={contentParsed[insuranceKey]}
          debug={debug}
          documents={documents}
          hideDisabled={hideDisabled}
          icon={components.icon || ''}
          id={insuranceKey}
          identifyObject={identifyObject}
          inputHelps={inputHelps}
          isBackOffice={isBackOffice}
          key={insuranceKey}
          narrow={narrow}
          onChangeSupplementaryInsurances={this.handleChangeSupplementaryInsurances}
          onChangeUserFormData={this.handleChangeUserFormData}
          onChangeValue={this.handleOnChange}
          onQuoteValidate={onQuoteValidate}
          onRefreshCoveragePrices={this.handleRefreshCoveragePrices}
          onRemoveClick={this.handleRemoveInsurance}
          parent={!!components.children}
          pushIsPristine={pushIsPristine}
          resources={resources}
          specification={specificationParsed[insuranceKey]}
          supplementaryInsurances={supplementaryInsurances}
          tariffContent={tariffContent}
          templateVariables={templateVariables}
          title={contentParsed[insuranceKey].fieldNames[insuranceKey]}
          token={token}
          validationErrors={validationErrors}
          valueNames={valueNames}
        />
      )
    })
  }
}
